// @flow
import * as React from "react"
import axios from "axios"
import { hot } from "react-hot-loader"
import { tRouteProps, tDefaultTemplate } from "../types"
import { Container } from "../components/StyledComponents"
import { css } from "emotion/macro"
import { FlexibleContent } from "../components/FlexibleContent"
import { color } from "../styles/variables"
import * as emailValidator from "email-validator"
import { PageMarquee } from "../components/PageMarquee/index"
import { Layout } from "../components/Layout"
import * as Sentry from "@sentry/browser"
import { PageMeta } from "../components/PageMeta"

type tPrayerRequestForm = {
  title: string
}

const form = css`
  width: 500px;
  max-width: 500%;
  margin-left: auto;
  margin-right: auto;
  margin-top: 30px;

  &,
  * {
    box-sizing: border-box;
  }

  input {
    font-size: 14px;
    padding: 10px;
    line-height: 26px;

    &[type="text"],
    &[type="email"] {
      width: 100%;
    }

    &[type="radio"] + label {
      margin-left: 3px;
    }

    &:focus {
      outline: 1px solid ${color.rgb.purple[1]};
    }
  }
`

const sectionLabel = css`
  width: 100%;
  display: block;
  margin-top: 20px;
  padding: 10px;
  background-color: ${color.rgba.purple(0.05)[1]};
  font-size: 16px;
`

const itemLabel = css``
const fieldSection = css``
const inputSection = css``
const radioSection = css``

const formMessage = css`
  margin-top: 20px;
`

const radioOption = css`
  display: flex;
  margin: 10px 0;
  align-items: center;
  font-size: 14px;
`

const radioOptions = css``

const formError = css`
  background-color: ${color.rgba.red(0.05)[0]};
  color ${color.rgb.red[0]};
  font-size: 12px;
  padding: 10px;
`

const formSuccess = css`
  background-color: ${color.rgba.green(0.05)[0]};
  color ${color.rgb.green[0]};
  font-size: 12px;
  padding: 10px;
`

const submitButton = css`
  width: 100%;
  margin-top: 20px;
`

const Required = ({ display }: { display: boolean }) =>
  display ? (
    <span
      className={css`
        color: ${color.rgb.red[0]};
        margin-right: 5px;
        font-size: 10px;
      `}
    >
      *
    </span>
  ) : null

class PrayerRequestForm extends React.Component<
  tPrayerRequestForm & tRouteProps & tDefaultTemplate,
  {
    values: {
      name: string,
      requestIsFor: "myself" | "another",
      requestIsForAnother: string,
      forTheGood: string
    },
    errors: {
      name: string,
      requestIsFor: string,
      requestIsForAnother: string,
      forTheGood: string
    },
    required: {
      name: true,
      requestIsFor: true,
      requestIsForAnother: any,
      forTheGood: true
    },
    submitValidation: {
      type: "" | "success" | "error",
      message: string
    }
  }
> {
  requestIsForAnotherIsRequired = (requestIsFor) => {
    return requestIsFor === "another" ? true : false
  }
  initialState = {
    values: {
      name: "",
      requestIsFor: "myself",
      requestIsForAnother: "",
      forTheGood: ""
    },
    errors: {
      name: "",
      requestIsFor: "",
      requestIsForAnother: "",
      forTheGood: ""
    },
    required: {
      name: true,
      requestIsFor: true,
      forTheGood: true
    },
    submitValidation: {
      type: "",
      message: ""
    }
  }
  state = {
    values: this.initialState.values,
    errors: this.initialState.errors,
    submitValidation: this.initialState.submitValidation,
    required: {
      ...this.initialState.required,
      requestIsForAnother: () =>
        this.requestIsForAnotherIsRequired(this.state.values.requestIsFor)
    }
  }
  handleValueChange = (field, event) => {
    event.persist()
    this.setState((st) => ({
      values: {
        ...st.values,
        [field]: event.target.value
      }
    }))
  }
  handleSubmit = async () => {
    const errors = {}
    let errorFound = false

    Object.keys(this.state.required).map((key) => {
      const mightBeRequired = this.state.required[key]
      const isRequired =
        typeof mightBeRequired === "function"
          ? mightBeRequired()
          : mightBeRequired

      if (isRequired && !this.state.values[key]) {
        errors[key] = "This field is required."
        errorFound = true
      }
    })

    if (errorFound) {
      this.setState({
        errors: {
          ...this.initialState.errors,
          ...errors
        }
      })
    } else {
      try {
        const { data } = await axios.post(
          "/.netlify/functions/prayer-request",
          this.state.values
        )

        if (data) {
          this.setState({
            errors: this.initialState.errors,
            values: this.initialState.values,
            submitValidation: {
              type: "success",
              message: `Your prayer request was successfully submitted!`
            }
          })
        } else {
          this.setServerError(data)
        }
      } catch (error) {
        this.setServerError(error)
      }
    }
  }
  setServerError = (error) => {
    Sentry.captureException(error)
    this.setState({
      submitValidation: {
        type: "error",
        message:
          "Sorry, there was a problem submitting your request. Our development team has been notified. Please try again later, or you may also leave a Prayer Request voicemail at 206-524-7729"
      }
    })
  }
  render() {
    return (
      <React.Fragment>
        {this.props.default_template_content && (
          <PageMarquee
            image={this.props.default_template_content.marquee_image}
          />
        )}
        <PageMeta
          title="Prayer Request Form"
          description=""
          image={this.props.brand.logo}
        />
        <Container
          className={css`
            position: relative;
          `}
        >
          <section
            className={css`
              padding-top: 60px;
            `}
          >
            <h2 className="text-center">Prayer Request Form</h2>
            {this.state.submitValidation.type !== "success" ? (
              <React.Fragment>
                <div className={form}>
                  <div className={inputSection + " " + fieldSection}>
                    <label htmlFor="name" className={sectionLabel}>
                      <Required display={this.state.required.name} />
                      Name
                    </label>
                    <input
                      type="text"
                      placeholder="name"
                      required
                      value={this.state.values.name}
                      onChange={(e) => this.handleValueChange("name", e)}
                    />
                    {this.state.errors.name && (
                      <div className={formError}>{this.state.errors.name}</div>
                    )}
                  </div>
                  <div className={fieldSection + " " + radioSection}>
                    <span className={sectionLabel}>
                      <Required display={this.state.required.requestIsFor} />
                      Request is for:
                    </span>
                    <div className={radioOptions}>
                      <div className={radioOption}>
                        <input
                          type="radio"
                          id="myself"
                          name="request-is-for"
                          value="myself"
                          onChange={(e) =>
                            this.handleValueChange("requestIsFor", e)
                          }
                          checked={this.state.values.requestIsFor === "myself"}
                        />
                        <label htmlFor="myself" className={itemLabel}>
                          Myself
                        </label>
                      </div>
                      <div className={radioOption}>
                        <input
                          type="radio"
                          id="another"
                          name="request-is-for"
                          value="another"
                          onChange={(e) =>
                            this.handleValueChange("requestIsFor", e)
                          }
                          checked={this.state.values.requestIsFor === "another"}
                        />
                        <label htmlFor="another" className={itemLabel}>
                          Another
                        </label>
                      </div>
                    </div>
                    {this.state.errors.requestIsFor && (
                      <div className={formError}>
                        {this.state.errors.requestIsFor}
                      </div>
                    )}
                  </div>
                  {this.state.values.requestIsFor === "another" && (
                    <div className={inputSection + " " + fieldSection}>
                      <label
                        htmlFor="requestIsForAnother"
                        className={sectionLabel}
                      >
                        <Required
                          display={this.state.required.requestIsForAnother()}
                        />
                        Name of who the request is for
                      </label>
                      <input
                        type="text"
                        placeholder="name"
                        onChange={(e) =>
                          this.handleValueChange("requestIsForAnother", e)
                        }
                        value={this.state.values.requestIsForAnother}
                      />
                      {this.state.errors.requestIsForAnother && (
                        <div className={formError}>
                          {this.state.errors.requestIsForAnother}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={inputSection + " " + fieldSection}>
                    <label htmlFor="name" className={sectionLabel}>
                      <Required display={this.state.required.forTheGood} />
                      For the Good of all concerned, I ask for and accept
                    </label>
                    <input
                      type="text"
                      placeholder="i.e. Love, Prosperity, Wisdom, etc."
                      required
                      value={this.state.values.forTheGood}
                      onChange={(e) => this.handleValueChange("forTheGood", e)}
                    />
                    {this.state.errors.forTheGood && (
                      <div className={formError}>
                        {this.state.errors.forTheGood}
                      </div>
                    )}
                  </div>
                  {this.state.submitValidation.message && (
                    <div
                      className={
                        formMessage +
                        " " +
                        (this.state.submitValidation.type === "error"
                          ? formError
                          : formSuccess)
                      }
                    >
                      {this.state.submitValidation.message}
                    </div>
                  )}
                  <div className={inputSection}>
                    <button
                      onClick={this.handleSubmit}
                      className={"btn btn-purple " + submitButton}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <div>
                <p className="text-center">
                  We have received your Prayer Request and our Prayer Team is
                  praying on it. Many blessings, CSL Prayer Team.
                </p>
              </div>
            )}
          </section>
          {this.props.flexible_content && (
            <FlexibleContent items={this.props.flexible_content} />
          )}
        </Container>
      </React.Fragment>
    )
  }
}

export const RenderPrayerRequestForm = () => (
  <Layout>{(routeProps) => <PrayerRequestForm {...routeProps} />}</Layout>
)

export default hot(module)(RenderPrayerRequestForm)



// WEBPACK FOOTER //
// /opt/build/repo/src/screens/PrayerRequestForm.js